import type { ApplyChannel } from '@/components/v2/tools/amazon-listing/RemakeApply/RemakeApplyChannelPicker'
import { ChannelFilterSelector } from './ChannelFilterSelector'
import { AddToProductSlotGrid, type SlotFill } from './AddToProductSlotGrid'
import type { MediaSize } from './MediaSizeToggle'
import type { AddToProductTarget } from '../types'

interface SingleChannelViewProps {
  channels: ApplyChannel[]
  activeId: string
  onChange: (id: string) => void
  filled: Record<number, SlotFill>
  size?: MediaSize
  onDelete?: (target: AddToProductTarget) => void
}

function ChannelHeader({ channel }: { channel: ApplyChannel }) {
  return (
    <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
      <span>{channel.name}</span>
      {channel.externalChannelId && (
        <span className="font-mono text-xs text-neutral-500">
          {channel.externalChannelId}
        </span>
      )}
    </h4>
  )
}

/**
 * Single-channel view: a channel filter plus exactly one slot grid for the
 * active channel. Renders the same channel-name + external-id header as
 * AllChannelsView above the grid.
 */
export function SingleChannelView({
  channels,
  activeId,
  onChange,
  filled,
  size = 'M',
  onDelete,
}: SingleChannelViewProps) {
  const active = channels.find((c) => c.id === activeId)
  const selector = (
    <ChannelFilterSelector
      channels={channels}
      activeId={activeId}
      onChange={onChange}
    />
  )
  if (!activeId || !active) {
    return <div className="space-y-4">{selector}</div>
  }
  return (
    <div className="space-y-4">
      {selector}
      <ChannelHeader channel={active} />
      <AddToProductSlotGrid
        filled={filled}
        channelId={activeId}
        size={size}
        onDelete={onDelete}
      />
    </div>
  )
}
